import { ImageInNode, ImageType, OptionPair } from "@/types/productTypes";
import { produce } from "immer";

import fetchDb from "../utils/customRequestFunction";
import { getStage } from "../utils/getFunction";

import { AdminSliceCreator, AdminStoreState } from "./useAdminStore";

export interface ImageStoreState {
   images: ImageType[];
   selectedImage: ImageType | null;
   allImages: ImageType[];
   currentConfigs: OptionPair[];
   imagePage: boolean;
   fetchImages: () => Promise<void>;
   fetchConfigs: (productId: number) => Promise<void>;
   uploadImage: (
      file: File,
      stageId: number,
      folderId?: number
   ) => Promise<void>;
   moveImage: (
      draggedImageId: number,
      dropId: number,
      fromItemPairId: number
   ) => Promise<void>;
   setMainImage: (
      draggedImageId: number,
      fromItemPairId: number,
      optionPairId: number
   ) => Promise<void>;
   addConfig: (
      productId: number,
      stageId: number,
      name: string,
      optionIds: number[]
   ) => Promise<void>;
   selectConfig: (
      productId: number,
      stageId: number,
      configId: number
   ) => Promise<void>;
   setImagePage: (selected: boolean) => void;
   latestMoveRequestId: number;
   fetchOptionPair: (stageId: number) => Promise<void>;
   optionPair: OptionPair | null;
   cloneAndAppendConfig: (
      productId: number,
      stageId: number,
      baseConfigId: number,
      name: string,
      optionIds: number[]
   ) => Promise<void>;
   detachItemPairs: (
      optionPairOptionId: number,
      itemPairIds: number[]
   ) => Promise<void>;
   fetchAllImagesInProduct: (mainProductId: number) => Promise<void>;
   imagesInProduct: ImageType[];
   getImagesInStage: (stageId: number) => Promise<ImageType[]>;
   optionPairs: OptionPair[];
   fetchProductOptionPairs: (mainProductId: number) => Promise<void>;
   // New: assign an image to a folder
   assignImageToFolder: (
      imageId: number,
      folderId: number | null
   ) => Promise<void>;
   imageManagerPage: boolean;
   setImageManagerPage: (selected: boolean) => void;
   toggleImagePage: () => void;
   toggleImageManagerPage: () => void;
   // New: update and delete image
   updateImage: (id: number, data: Partial<ImageType>) => Promise<ImageType>;
   deleteImage: (id: number) => Promise<void>;
   setOptimisticImages: (images: ImageType[]) => void;
   uploadImages: (
      files: File[],
      stageId: number,
      folderId?: number
   ) => Promise<void>;
   setOptimisticNodeImages: (nodeId: number, images: ImageInNode[]) => void;
   assignImageToNode: (
      nodeId: number,
      imageId: number,
      itemId?: number
   ) => Promise<void>;
}

export const createImageSlice: AdminSliceCreator<ImageStoreState> = (
   set,
   get
) => ({
   images: [],
   selectedImage: null,
   allImages: [],
   imagesInProduct: [],
   imagePage: false,
   imageManagerPage: false,
   currentConfigs: [],
   latestMoveRequestId: 0,
   optionPair: null,
   optionPairs: [],

   async fetchProductOptionPairs(mainProductId) {
      try {
         console.log("fetchedOptionPairs");
         const fetchedOptionPairs = await fetchDb.get(
            `/api/option-pair/product/${mainProductId}`
         );
         console.log("fetchedOptionPairs", fetchedOptionPairs);
         if (fetchedOptionPairs) {
            set(
               produce((state: AdminStoreState) => {
                  state.optionPairs = fetchedOptionPairs;
               })
            );
         }
      } catch (err) {
         set(
            produce((state) => {
               state.optionPairs = [];
            })
         );
      }
   },

   async fetchAllImagesInProduct(mainProductId) {
      try {
         const images = (await fetchDb.get(
            `/api/images/mainProduct/${mainProductId}`
         )) as ImageType[];
         console.log("allImages", images);
         set(
            produce((state) => {
               state.imagesInProduct = images;
            })
         );
      } catch (err) {
         set(
            produce((state) => {
               state.imagesInProduct = [];
            })
         );
      }
   },
   async getImagesInStage(stageId: number) {
      console.log("imagesInProduct", get().imagesInProduct);
      return get().imagesInProduct.filter(
         (image) => image.stage?.id === stageId
      );
   },

   async detachItemPairs(optionPairOptionId, itemPairIds) {
      if (itemPairIds.length === 0) return;
      await fetchDb.post("/api/option-pair/unpairItemPair", {
         childOptionPairOptionId: optionPairOptionId,
         unpairItemPairId: itemPairIds[0],
      });
   },

   setImagePage: (selected) => {
      console.log("setting image", selected);
      set(
         produce((state) => {
            state.imagePage = selected;
         })
      );
   },
   setImageManagerPage: (selected) => {
      console.log("setting imageManagerPage", selected);
      set(
         produce((state) => {
            state.imageManagerPage = selected;
         })
      );
   },
   toggleImagePage: () => {
      get().setImageManagerPage(!get().imagePage);
   },
   toggleImageManagerPage: () => {
      get().setImageManagerPage(!get().imageManagerPage);
   },
   fetchImages: async () => {
      try {
         const images = await fetchDb.get(`/api/images`);
         set(
            produce((state) => {
               state.allImages = images;
            })
         );
      } catch (error) {
         console.error("Error fetching images:", error);
      }
   },
   fetchConfigs: async (productId) => {
      try {
         const configs = await fetchDb.get(
            `/api/option-pair/product/${productId}`
         );
         set(
            produce((state) => {
               state.currentConfigs = configs;
            })
         );
      } catch (error) {
         console.error("Error fetching configs:", error);
      }
   },
   fetchOptionPair: async (stageId) => {
      try {
         const response = (await fetchDb.get(
            `/api/option-pair/stage/${stageId}`
         )) as OptionPair;
         set(
            produce((state) => {
               state.optionPair = response;
            })
         );
      } catch (error) {
         set(
            produce((state) => {
               state.optionPair = null;
            })
         );
      }
   },
   uploadImage: async (file, stageId, folderId?: number) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "configurator-image-preset");
      try {
         const response = await fetch(
            "https://api.cloudinary.com/v1_1/dxgc5hsrr/image/upload",
            { method: "POST", body: formData }
         );
         const data = await response.json();

         // Get the file name without extension
         const fileName = file.name.replace(/\.[^/.]+$/, "");

         const newImage = await fetchDb.post("/api/images", {
            url: data.secure_url,
            title: fileName,
            relativePos: get().allImages.length,
            stageId: stageId,
            ...(folderId ? { folder: { connect: { id: folderId } } } : {}),
         });

         set(
            produce((state) => {
               state.allImages.push(newImage);
            })
         );
      } catch (error) {
         console.error("Error uploading image:", error);
         throw error;
      }
   },
   moveImage: async (imageId: number, itemPairId: number, nodeId: number) => {
      const currentNode = get().currentOptionPair?.nodes.find(
         (n) => n.id === nodeId
      );
      const currentImages = currentNode?.imagesInNode || [];
      try {
         set(
            produce((state: AdminStoreState) => {
               const node = state.currentOptionPair?.nodes.find(
                  (n) => n.id === nodeId
               );
               if (node) {
                  if (itemPairId === -1) {
                     node.imagesInNode = node.imagesInNode.filter(
                        (img) => img.image.id !== imageId
                     );
                  } else {
                     const image = state.allImages.find(
                        (img) => img.id === imageId
                     );
                     if (image) {
                        const newImageInNode: ImageInNode = {
                           id: imageId,
                           image,
                           nodeId,
                           itemId: itemPairId,
                           imageId: imageId,
                           zIndex: image.relativePos,
                           node: node,
                        };
                        const existingIndex = node.imagesInNode.findIndex(
                           (img) => img.itemId === itemPairId
                        );
                        if (existingIndex !== -1) {
                           node.imagesInNode[existingIndex] = newImageInNode;
                        } else {
                           node.imagesInNode.push(newImageInNode);
                        }
                     }
                  }
               }
            })
         );

         await fetchDb.patch("/api/graph/item-pair-image", {
            nodeId,
            imageId,
            itemId: itemPairId === -1 ? null : itemPairId,
         });
      } catch (error) {
         set(
            produce((state: AdminStoreState) => {
               const node = state.currentOptionPair?.nodes.find(
                  (n) => n.id === nodeId
               );
               if (node) {
                  node.imagesInNode = currentImages;
               }
            })
         );
         console.error("Error moving image:", error);
         throw error;
      }
   },
   assignImageToNode: async (nodeId, imageId, itemId?) => {
      await fetchDb.patch("/api/option-pair/item-pair-image", {
         nodeId,
         imageId,
         itemId,
      });
      const stageId = get().optionPair?.stages?.[0]?.id;
      if (stageId) {
         await get().fetchOptionPair(stageId);
      }
   },
   setMainImage: async (draggedImageId, fromItemPairId, optionPairId) => {
      try {
         await fetchDb.patch("/api/option-pair/mainImage", {
            imageId: draggedImageId,
            fromItemPairId,
            optionPairId,
         });
      } catch (error) {
         console.error("Error setting main image:", error);
      }
   },
   addConfig: async (productId, stageId, name, optionIds) => {
      const newConfigData = { name, stageId, optionIds };
      try {
         try {
            await fetchDb.post("/api/option-pair", {
               ...newConfigData,
            });
         } catch (error) {
            console.error("Error creating config:", error);
         }
         console;
         await get().fetchProducts?.();
         await get().fetchConfigs?.(productId);
         await get().fetchAllOptions?.(productId);
      } catch (error) {
         console.error("Error creating config:", error);
      }
   },
   selectConfig: async (productId, stageId, configId) => {
      set(
         produce((state) => {
            const stage = getStage(state.allProducts, productId, stageId);
            if (!stage) return;
            stage.optionPairId = configId;
            stage.optionPair =
               get().currentConfigs.find((config) => config.id === configId) ??
               undefined;
         })
      );
      await fetchDb.patch(`/api/option-pair/stage/${stageId}`, {
         optionId: configId,
      });
   },
   cloneAndAppendConfig: async (
      productId,
      stageId,
      baseConfigId,
      name,
      optionIds
   ) => {
      try {
         const payload = {
            baseOptionPairId: baseConfigId,
            newOptionIds: optionIds,
            stageId,
            name,
         };
         const createdConfig = await fetchDb.post(
            "/api/option-pair/clone",
            payload
         );
         if (createdConfig) {
            await get().fetchProducts?.();
            await get().fetchConfigs?.(productId);
            await get().fetchAllOptions?.(productId);
         }
      } catch (error) {
         console.error("Error cloning & appending config:", error);
      }
   },
   setOptimisticImages: (newImages: ImageType[]) => {
      set(
         produce((draft: AdminStoreState) => {
            draft.images = newImages;
         })
      );
   },
   assignImageToFolder: async (imageId: number, folderId: number | null) => {
      try {
         const updatedImage = await fetchDb.patch(
            `/api/images/${imageId}/folder`,
            { folderId: folderId || null }
         );
         return updatedImage;
      } catch (error) {
         console.error("Error assigning image to folder:", error);
         throw error;
      }
   },
   updateImage: async (id, data) => {
      try {
         const updatedImage = await fetchDb.patch(`/api/images/${id}`, data);
         set(
            produce((state: AdminStoreState) => {
               const index = state.allImages.findIndex((img) => img.id === id);
               if (index !== -1) {
                  state.allImages[index] = updatedImage;
               }
            })
         );
         return updatedImage;
      } catch (error) {
         console.error("Error updating image:", error);
         throw error;
      }
   },
   deleteImage: async (id) => {
      try {
         await fetchDb.delete(`/api/images/${id}`);
         set(
            produce((state: AdminStoreState) => {
               state.allImages = state.allImages.filter((img) => img.id !== id);
            })
         );
      } catch (error) {
         console.error("Error deleting image:", error);
         throw error;
      }
   },
   uploadImages: async (files, stageId, folderId) => {
      try {
         const uploadPromises = files.map(async (file) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("upload_preset", "configurator-image-preset");

            const response = await fetch(
               "https://api.cloudinary.com/v1_1/dxgc5hsrr/image/upload",
               { method: "POST", body: formData }
            );
            const data = await response.json();

            const fileName = file.name.replace(/\.[^/.]+$/, "");

            const newImage = await fetchDb.post("/api/images", {
               url: data.secure_url,
               title: fileName,
               relativePos: get().allImages.length,
               stageId: stageId,
               ...(folderId ? { folder: { connect: { id: folderId } } } : {}),
            });

            return newImage;
         });

         const newImages = await Promise.all(uploadPromises);

         set(
            produce((state: AdminStoreState) => {
               state.allImages = [...state.allImages, ...newImages];
            })
         );
      } catch (error) {
         console.error("Error uploading images:", error);
         throw error;
      }
   },
   setOptimisticNodeImages: (nodeId: number, images: ImageInNode[]) => {
      set(
         produce((state: AdminStoreState) => {
            const optionPair = state.currentOptionPair;
            if (optionPair) {
               const node = optionPair.nodes.find((n) => n.id === nodeId);
               if (node) {
                  node.imagesInNode = images;
               }
            }
         })
      );
   },
});
